// Contrato compartido con SCAPP: cada clave representa una magnitud distinta.
export const FEEDING_RATES = {
  tasa: { unit: 'kg/min', graph: 'graph_feeding' },
  tasa_pfm: { unit: 'PPM', graph: 'graph_feeding_pfm' },
  tasa_kgtonmin: { unit: 'kg/ton/min', graph: 'graph_feeding_kgtonmin' },
  tasa_ppv: { unit: 'PPV', graph: 'graph_feeding_ppv' },
};

const rateKeys = Object.keys(FEEDING_RATES);
const isRatePoint = (point) => typeof point?.value === 'number' && Number.isFinite(point.value) && point.value >= 0;
const hasSamples = (points) => Array.isArray(points) && points.some(isRatePoint);
const hasSignal = (points) => Array.isArray(points) && points.some((point) => isRatePoint(point) && point.value > 0);

export const rateDays = (node) => [...new Set([
  ...(node?.daily_values_summary || node?.values_summary || []),
  ...rateKeys.flatMap((key) => [
    ...(node?.[FEEDING_RATES[key].graph] || []),
    ...(node?.['alarms_by_' + key] || []),
  ]),
].map((entry) => entry?.day).filter(Boolean))];

export const availableRateKeys = (nodes) => rateKeys.filter((key) => nodes.some((node) =>
  (node?.[FEEDING_RATES[key].graph] || []).some((entry) => hasSamples(entry?.value))));

export function resolveRateKey(node, day, preference = 'auto') {
  if (FEEDING_RATES[preference]) return preference;
  const points = (key) => node?.[FEEDING_RATES[key].graph]?.find((entry) => entry.day === day)?.value;
  const available = rateKeys.filter((key) => hasSamples(points(key)));
  const summary = (node?.daily_values_summary || node?.values_summary)?.find((entry) => entry.day === day)?.value || {};
  const declared = [summary.effective_rate_view, summary.default_rate_view]
    .find((key) => available.includes(key));
  if (declared) return declared;
  const candidates = available.filter((key) => hasSignal(points(key)));
  // Los resúmenes de área antiguos sólo incluyen las alarmas, sin curvas.
  return candidates[0] || available[0] || rateKeys.find((key) =>
    node?.['alarms_by_' + key]?.some((entry) => entry.day === day)) || 'tasa';
}

export const rateAlarmEntries = (node, preference = 'auto') => rateDays(node).flatMap((day) => {
  const key = resolveRateKey(node, day, preference);
  const entry = node?.['alarms_by_' + key]?.find((item) => item.day === day);
  return entry ? [{ ...entry, rateKey: key, unit: FEEDING_RATES[key].unit }] : [];
});

// Normalizar antes de compactar/agrupar evita mezclar kg/min y PFM históricos.
// Legacy sólo conserva la última unidad diaria; cambios intradía requieren recálculo en SCAPP.
export function normalizeRateAnalytics(data) {
  if (!data?.analytic_data) return data;
  const aliases = { 'kg/min': 'tasa', ppm: 'tasa_pfm', pfm: 'tasa_pfm',
    'pellet/pez/min': 'tasa_pfm', 'pellets/fish/min': 'tasa_pfm',
    'kg/ton/min': 'tasa_kgtonmin', ppv: 'tasa_ppv', 'pellet/pez/visita': 'tasa_ppv' };
  const analytic_data = Object.fromEntries(Object.entries(data.analytic_data).map(([id, original]) => {
    let node = original;
    (original.values_summary || []).forEach(({ day, value: summary }) => {
      const target = aliases[String(summary?.tasa_units || '').trim().toLowerCase()];
      if (!target || target === 'tasa') return;
      if (node === original) node = { ...original };
      const suffix = target.slice(4);
      const explicitPoints = node[FEEDING_RATES[target].graph]?.find((entry) => entry.day === day)?.value;
      const legacyPoints = node.graph_feeding?.find((entry) => entry.day === day)?.value;
      const explicit = hasSamples(explicitPoints) && (hasSignal(explicitPoints) || !hasSignal(legacyPoints));
      for (const [source, destination] of [
        ['graph_feeding', FEEDING_RATES[target].graph],
        ['tasa_values', target + '_values'],
        ['alarms_by_tasa', 'alarms_by_' + target],
        ['alarms_by_tasa_levels', 'alarms_by_tasa_levels' + suffix],
      ]) {
        const entry = node[source]?.find((item) => item.day === day);
        if (!entry) continue;
        node[source] = node[source].filter((item) => item.day !== day);
        if (!explicit) node[destination] = [...(node[destination] || []).filter((item) => item.day !== day), entry];
      }
      node.values_summary = node.values_summary.map((entry) => entry.day !== day ? entry : {
        ...entry, value: { ...entry.value,
          effective_rate_view: entry.value.effective_rate_view === 'tasa' ? target : entry.value.effective_rate_view,
          default_rate_view: entry.value.default_rate_view === 'tasa' ? target : entry.value.default_rate_view,
        },
      });
      // El formato antiguo guardaba la tasa genérica también en el tooltip.
      Object.keys(node).filter((key) => key.startsWith('graph_') && Array.isArray(node[key])).forEach((key) => {
        node[key] = node[key].map((entry) => entry.day !== day || !Array.isArray(entry.value) ? entry : {
          ...entry, value: entry.value.map((point) => !point.snapshot || point.snapshot.tasa === undefined ? point : {
            ...point, snapshot: { ...point.snapshot, [target]: point.snapshot[target] ?? point.snapshot.tasa, tasa: undefined },
          }),
        });
      });
    });
    return [id, node];
  }));
  return { ...data, analytic_data };
}
